import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message, Dropdown } from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getSocialPosts,
  deleteSocialPost,
  publishSocialPost,
  syncPostEngagement,
  type SocialPost,
  type SocialPostFilters,
} from '@/api/socialApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  scheduled: 'orange',
  published: 'green',
  failed: 'red',
};

const PLATFORM_COLORS: Record<string, string> = {
  facebook: 'blue',
  instagram: 'magenta',
  twitter: 'cyan',
  linkedin: 'geekblue',
  wechat: 'green',
};

const POST_TYPE_COLORS: Record<string, string> = {
  text: 'default',
  image: 'purple',
  video: 'volcano',
  link: 'cyan',
};

export default function SocialPostListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<SocialPostFilters>({});

  const canWrite = hasPermission('social_posts', 'write');
  const canDelete = hasPermission('social_posts', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof SocialPostFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getSocialPosts({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('social.action.delete_confirm_post'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteSocialPost(id);
            message.success(t('social.delete_post_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handlePublish = useCallback(
    async (id: string) => {
      try {
        await publishSocialPost(id);
        message.success(t('social.action.publish_success'));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const handleSyncEngagement = useCallback(
    async (id: string) => {
      try {
        await syncPostEngagement(id);
        message.success(t('social.action.sync_engagement_success'));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const columns: ColumnsType<SocialPost> = useMemo(
    () => [
      {
        title: t('social.field.content'),
        dataIndex: 'content',
        key: 'content',
        sorter: true,
        ellipsis: true,
        render: (content: string, record: SocialPost) => (
          <a onClick={() => navigate(`/social/posts/${record.id}`)}>
            {content.substring(0, 60)}{content.length > 60 ? '...' : ''}
          </a>
        ),
      },
      {
        title: t('social.field.platform'),
        dataIndex: 'platform',
        key: 'platform',
        width: 110,
        render: (platform: string) => (
          <Tag color={PLATFORM_COLORS[platform] ?? 'default'}>
            {t(`social.platform.${platform}`, platform)}
          </Tag>
        ),
      },
      {
        title: t('social.field.post_type'),
        dataIndex: 'post_type',
        key: 'post_type',
        width: 90,
        render: (type: string) => (
          <Tag color={POST_TYPE_COLORS[type] ?? 'default'}>
            {t(`social.post_type.${type}`, type)}
          </Tag>
        ),
      },
      {
        title: t('social.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`social.status.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('social.field.likes'),
        dataIndex: ['engagement', 'likes'],
        key: 'likes',
        width: 70,
        render: (val: number) => val ?? 0,
      },
      {
        title: t('social.field.reach'),
        dataIndex: ['engagement', 'reach'],
        key: 'reach',
        width: 70,
        render: (val: number) => val ?? 0,
      },
      {
        title: t('social.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 190,
        render: (_: unknown, record: SocialPost) => {
          const lifecycleItems = [];
          if (record.status === 'draft' && canWrite) {
            lifecycleItems.push({
              key: 'publish',
              icon: <SendOutlined />,
              label: t('social.action.publish'),
              onClick: () => handlePublish(record.id),
            });
            lifecycleItems.push({
              key: 'schedule',
              icon: <ClockCircleOutlined />,
              label: t('social.action.schedule'),
              onClick: () => navigate(`/social/posts/${record.id}/edit`),
            });
          }
          if (record.status === 'published' && canWrite) {
            lifecycleItems.push({
              key: 'sync',
              icon: <SyncOutlined />,
              label: t('social.action.sync_engagement'),
              onClick: () => handleSyncEngagement(record.id),
            });
          }

          return (
            <Space size="small">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/social/posts/${record.id}`)}
              />
              {canWrite && record.status !== 'published' && (
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/social/posts/${record.id}/edit`)}
                />
              )}
              {canDelete && record.status !== 'published' && (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              )}
              {lifecycleItems.length > 0 && (
                <Dropdown menu={{ items: lifecycleItems }}>
                  <Button type="link" size="small" icon={<DownOutlined />} />
                </Dropdown>
              )}
            </Space>
          );
        },
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete, handlePublish, handleSyncEngagement]
  );

  const platformOptions = useMemo(
    () => [
      { value: '', label: t('social.filter.all') },
      { value: 'facebook', label: t('social.platform.facebook') },
      { value: 'instagram', label: t('social.platform.instagram') },
      { value: 'twitter', label: t('social.platform.twitter') },
      { value: 'linkedin', label: t('social.platform.linkedin') },
      { value: 'wechat', label: t('social.platform.wechat') },
    ],
    [t]
  );

  const postTypeOptions = useMemo(
    () => [
      { value: '', label: t('social.filter.all') },
      { value: 'text', label: t('social.post_type.text') },
      { value: 'image', label: t('social.post_type.image') },
      { value: 'video', label: t('social.post_type.video') },
      { value: 'link', label: t('social.post_type.link') },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('social.filter.all') },
      { value: 'draft', label: t('social.status.draft') },
      { value: 'scheduled', label: t('social.status.scheduled') },
      { value: 'published', label: t('social.status.published') },
      { value: 'failed', label: t('social.status.failed') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('social.filter.platform')}
        style={{ width: 140 }}
        allowClear
        options={platformOptions}
        onChange={(val) => handleFilterChange('platform', val || undefined)}
      />
      <Select
        placeholder={t('social.filter.post_type')}
        style={{ width: 120 }}
        allowClear
        options={postTypeOptions}
        onChange={(val) => handleFilterChange('post_type', val || undefined)}
      />
      <Select
        placeholder={t('social.filter.status')}
        style={{ width: 120 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
    </Space>
  );

  const toolbar = (
    <Space>
      {filterBar}
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/social/posts/new')}
        >
          {t('social.new_post')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('social.posts_title')}</h2>
      <DataGrid<SocialPost>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('social.search_posts_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/social/posts/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
